import ShareIcon from "@mui/icons-material/Share";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "Components/_Shared/Buttons/Button";
import { DialogModal } from "Components/_Shared/DialogModal";
import { ReactComponent as PdfIcon } from "Components/_Shared/Icons/Assets/pdf.svg";
import { Subheading } from "Components/_Shared/Subheading";
import {
  getAnalysisSharedOutputsQueryKey,
  useAnalysisSharedOutputs,
  useClientDetail,
  useEmailAnalysisPdf,
  useGetAnalysis,
  useGetAnalysisClients,
  useShareShannonAnalysisPdf,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemRelationType, RelatedClientDetailType, SharePdfErrorCode } from "Infrastructure/ApiZfp/Schema";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useContext, useState } from "react";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { ClientOutputItem } from "./ClientOutputItem";
import { ClientOutputType } from "./types";

const handleGeneratePdf = (analysisId: string, versionId: string) => {
  const documentUrl = `${window.location.origin}${AppRouting.AnalysisDocument.path(analysisId, versionId)}`;
  window.open(documentUrl, "_blank");
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const ResultDocumentsPane: React.FC = () => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const [openConfirmModalType, setOpenConfirmModalType] = useState<ClientOutputType | null>(null);
  const queryClient = useQueryClient();

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId);

  const client = clientRelationsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client);

  const { data: clientData } = useClientDetail(client?.guid ?? "", {
    query: { enabled: !!client?.guid, select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });
  const clientEmail = clientData && clientData.type !== RelatedClientDetailType.related ? clientData.email : null;

  // Only the client's contact person (KO) may publish the financial plan to Šanon - the logged-in user's advisor
  // has to match the contact person from the analysis detail.
  const { user } = useUserContext();
  const { data: analysis } = useGetAnalysis(analysisId, versionId);
  const contactPersonGuid = analysis?.clientContactPerson?.guid;
  const isContactPerson = !!user?.advisorGuid && !!contactPersonGuid && user.advisorGuid === contactPersonGuid;

  const invalidateDataPooling = async () => {
    queryClient.invalidateQueries({ queryKey: getAnalysisSharedOutputsQueryKey(analysisId, versionId) });

    await delay(2_000);
    queryClient.invalidateQueries({ queryKey: getAnalysisSharedOutputsQueryKey(analysisId, versionId) });

    await delay(20_000);
    queryClient.invalidateQueries({ queryKey: getAnalysisSharedOutputsQueryKey(analysisId, versionId) });
  };

  const { mutate: sendEmailMutate } = useEmailAnalysisPdf({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.ClientResult.PdfResult.Email.Sent));
        invalidateDataPooling();
      },
      onError: () => {
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      },
    },
  });

  const { mutate: shareShannonMutate } = useShareShannonAnalysisPdf({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.Enabled));
        invalidateDataPooling();
      },
      onError: (error) => {
        if (error?.code === SharePdfErrorCode.clients_not_found || error?.code === SharePdfErrorCode.client_not_found) {
          toast.error(t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.ClientNotFound));
        } else {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        }
      },
    },
  });

  const { data } = useAnalysisSharedOutputs(analysisId, versionId);

  const handleSendEmailPdf = () => {
    if (client?.guid) {
      sendEmailMutate({ analysisId, versionId });
      setOpenConfirmModalType(null);
    } else {
      toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
    }
  };

  const handleEnablePdfInMobileApp = () => {
    shareShannonMutate({ analysisId, versionId });
    setOpenConfirmModalType(null);
  };

  const clientOutputConfig: Record<ClientOutputType, { title: string; subtitle: string; primaryAction: () => void }> = {
    [ClientOutputType.Email]: {
      title: t(Resources.AnalysisDetail.ClientResult.PdfResult.Email.Confirm.Title),
      subtitle: t(Resources.AnalysisDetail.ClientResult.PdfResult.Email.Confirm.Text, { email: clientEmail }),
      primaryAction: handleSendEmailPdf,
    },
    [ClientOutputType.MobileApp]: {
      title: t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.Confirm.Title),
      subtitle: t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.Confirm.Text),
      primaryAction: handleEnablePdfInMobileApp,
    },
  };

  const clientOutputSelected = openConfirmModalType !== null ? clientOutputConfig[openConfirmModalType] : null;

  return (
    <>
      <Stack spacing={Spacing.XXXL} paddingBottom={40}>
        <Box>
          <Typography variant="h3">{t(Resources.AnalysisDetail.ClientResult.PdfResult.Title)}</Typography>
          <Typography variant="body2" color="secondary">
            {t(Resources.AnalysisDetail.ClientResult.PdfResult.Subtitle)}
          </Typography>
        </Box>
        <Stack spacing={Spacing.L} alignItems={"flex-start"}>
          <Subheading title={t(Resources.AnalysisDetail.ClientResult.PdfResult.Preview)} icon={<PdfIcon />} />
          <Box pl={Spacing.XXL}>
            <Button startAdornment={<PdfIcon />} onClick={() => handleGeneratePdf(analysisId, versionId)}>
              {t(Resources.AnalysisDetail.ClientResult.PdfResult.GeneratePdf)}
            </Button>
          </Box>
          <Box sx={{ width: "60%" }} py={Spacing.XL}>
            <Divider />
          </Box>
          <Subheading
            title={t(Resources.AnalysisDetail.ClientResult.PdfResult.ShareWithClient)}
            icon={<ShareIcon sx={{ fontSize: "18px" }} />}
          />

          {data && (
            <Stack spacing={Spacing.L} pl={Spacing.XXL}>
              <ClientOutputItem
                buttonText={t(Resources.AnalysisDetail.ClientResult.PdfResult.Email.Send)}
                type={ClientOutputType.Email}
                data={data}
                onClick={() => setOpenConfirmModalType(ClientOutputType.Email)}
                disabled={!clientEmail}
                tooltipTitle={!clientEmail ? t(Resources.AnalysisDetail.ClientResult.PdfResult.Email.EmailInvalid) : null}
              />

              <ClientOutputItem
                buttonText={t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.Enable)}
                type={ClientOutputType.MobileApp}
                data={data}
                onClick={() => setOpenConfirmModalType(ClientOutputType.MobileApp)}
                disabled={!isContactPerson}
                tooltipTitle={!isContactPerson ? t(Resources.AnalysisDetail.ClientResult.PdfResult.MobileApp.NotContactPerson) : null}
              />
            </Stack>
          )}
        </Stack>
      </Stack>

      <DialogModal
        isOpen={openConfirmModalType !== null}
        onClose={() => setOpenConfirmModalType(null)}
        title={clientOutputSelected?.title ?? ""}
        subtitle={clientOutputSelected?.subtitle ?? ""}
        primaryAction={<Button onClick={clientOutputSelected?.primaryAction}>{t(Resources.Common.Yes)}</Button>}
        secondaryAction={
          <Button color={"secondary"} onClick={() => setOpenConfirmModalType(null)}>
            {t(Resources.Common.No)}
          </Button>
        }
      />
    </>
  );
};
